import React, { createContext, useContext, useCallback } from 'react'
import { useReducer } from 'react'
import { checkCharacterNotEnglish } from 'commons/language'
import debounce from 'lodash.debounce'
import effects from 'app/effects/effects'

const actionType = {
  SET_DIRTY_GENERAL: 'SET_DIRTY_GENERAL',
  SET_DIRTY_CONTACT: 'SET_DIRTY_CONTACT',
  SET_IS_SHOW_TRANSLATE: 'SET_IS_SHOW_TRANSLATE',
  CLOSEW_WARNING_TRANSLATE: 'CLOSEW_WARNING_TRANSLATE',
  CLOSE_DETACT: 'CLOSE_DETACT',
  SET_GENERAL_RESET: 'SET_GENERAL_RESET',
  SET_CONTACT_RESET: 'SET_CONTACT_RESET',
  SET_ADDRESS_RESET: 'SET_ADDRESS_RESET',
  ON_FORM_ALL_RESET: 'ON_FORM_ALL_RESET',
  SET_DIRTY_EMPLOYER: 'SET_DIRTY_EMPLOYER',
  SET_DIRTY_ADDRESS: 'SET_DIRTY_ADDRESS',
  SET_EMPLOYER_RESET: 'SET_EMPLOYER_RESET',
  SET_DIRTY_SOCIAL: 'SET_DIRTY_SOCIAL',
  SET_SOCIAL_RESET: 'SET_SOCIAL_RESET',
}
const initialState = {
  generalDirty: false,
  contactDirty: false,
  addressDirty: false,
  employerDirty: false,
  socialDirty: false,
  isResetForm: false,
  isShowTranslate: false,
  isShowDetect: false,
}

function reducer(state, action) {
  switch (action.type) {
    case actionType.SET_DIRTY_GENERAL:
      return {
        ...state,
        generalDirty: true,
      }
    case actionType.SET_DIRTY_CONTACT:
      return {
        ...state,
        contactDirty: true,
      }
    case actionType.SET_DIRTY_ADDRESS:
      return {
        ...state,
        addressDirty: true,
      }
    case actionType.SET_DIRTY_SOCIAL:
      return {
        ...state,
        socialDirty: true,
      }
    case actionType.SET_DIRTY_EMPLOYER:
      return {
        ...state,
        employerDirty: true,
      }

    case actionType.SET_IS_SHOW_TRANSLATE:
      return {
        ...state,
        isShowTranslate: action.isShowTranslate,
        isShowDetect: action.isShowDetect,
      }
    case actionType.CLOSEW_WARNING_TRANSLATE:
      return {
        ...state,
        isShowTranslate: false,
      }
    case actionType.CLOSE_DETACT:
      return {
        ...state,
        isShowDetect: false,
      }
    case actionType.SET_GENERAL_RESET:
      return {
        ...state,
        generalDirty: false,
      }
    case actionType.SET_CONTACT_RESET:
      return {
        ...state,
        contactDirty: false,
      }

    case actionType.SET_ADDRESS_RESET:
      return {
        ...state,
        addressDirty: false,
      }
    case actionType.SET_EMPLOYER_RESET:
      return {
        ...state,
        employerDirty: false,
      }
    case actionType.SET_SOCIAL_RESET:
      return {
        ...state,
        socialDirty: false,
      }

    case actionType.ON_FORM_ALL_RESET:
      return {
        ...state,
        generalDirty: false,
        contactDirty: false,
        addressDirty: false,
        employerDirty: false,
        socialDirty: false,
      }

    default: {
      throw new Error()
    }
  }
}

const FormContext = createContext(null)

export const TEXT_10_MAX = 10
export const TEXT_50_MAX = 50
export const TEXT_200_MAX = 200
export const PHONETIC_TEXT_MAX = 100
export const TEXT_255_MAX = 255

export const useFormContext = () => {
  return useContext(FormContext)
}

export const FormProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState)

  const onGeneralChange = () => {
    dispatch({ type: actionType.SET_DIRTY_GENERAL })
  }

  const onContactChange = () => {
    dispatch({ type: actionType.SET_DIRTY_CONTACT })
  }

  const onAddressChange = () => {
    dispatch({ type: actionType.SET_DIRTY_ADDRESS })
  }

  const onEmployerChange = () => {
    dispatch({ type: actionType.SET_DIRTY_EMPLOYER })
  }

  const onSocialChange = () => {
    dispatch({ type: actionType.SET_DIRTY_SOCIAL })
  }

  const onGeneralReset = () => {
    dispatch({
      type: actionType.SET_GENERAL_RESET,
    })
  }

  const onContactReset = () => {
    dispatch({
      type: actionType.SET_CONTACT_RESET,
    })
  }

  const onAddressReset = () => {
    dispatch({
      type: actionType.SET_ADDRESS_RESET,
    })
  }

  const onEmployerReset = () => {
    dispatch({
      type: actionType.SET_EMPLOYER_RESET,
    })
  }
  const onSocialReset = () => {
    dispatch({
      type: actionType.SET_SOCIAL_RESET,
    })
  }

  const checkTranslate = useCallback(
    debounce((string) => {
      const isTranslate = checkCharacterNotEnglish(string)
      dispatch({
        type: actionType.SET_IS_SHOW_TRANSLATE,
        isShowTranslate: false,
        isShowDetect: isTranslate,
      })
    }, 500),
    [],
  )
  const checkTranslateFirstState = useCallback(
    debounce((string) => {
      const isTranslate = checkCharacterNotEnglish(string)

      dispatch({
        type: actionType.SET_IS_SHOW_TRANSLATE,
        isShowTranslate: isTranslate,
        isShowDetect: false,
      })
    }, 500),
    [],
  )

  const onAllFormReset = () => {
    dispatch({
      type: actionType.ON_FORM_ALL_RESET,
      payload: {
        isResetForm: false,
        generalDirty: false,
        contactDirty: false,
        addressDirty: false,
        employerDirty: false,
        socialDirty: false,
      },
    })
  }

  const onTranslate = async ({
    subscriberId,
    firstName,
    lastName,
    middleName,
  }) => {
    try {
      const res = await effects.apis.translateSubscriberFullName({
        subscriberId,
        data: {
          firstName,
          lastName,
          middleName,
          sync: 1,
        },
      })

      const { phoneticFirstName, phoneticMiddleName, phoneticLastName } = res
      return { phoneticFirstName, phoneticMiddleName, phoneticLastName }
    } catch (error) {
      return {
        phoneticFirstName: '',
        phoneticMiddleName: '',
        phoneticLastName: '',
      }
    }
  }

  const closeWarningTranslate = () => {
    dispatch({ type: actionType.CLOSEW_WARNING_TRANSLATE })
  }

  const closeDetact = () => {
    dispatch({ type: actionType.CLOSE_DETACT })
  }

  const dataExport = {
    ...state,
    onGeneralChange,
    onContactChange,
    onAddressChange,
    onEmployerChange,
    onSocialChange,
    checkTranslate,
    checkTranslateFirstState,
    onTranslate,
    closeWarningTranslate,
    closeDetact,
    onAllFormReset,
    onGeneralReset,
    onContactReset,
    onAddressReset,
    onEmployerReset,
    onSocialReset,
  }

  return (
    <FormContext.Provider value={dataExport}>{children}</FormContext.Provider>
  )
}
